import { type FC, useEffect } from 'react'

import { Expand } from '@liveHelper/assets/Expand'
import { Minimize } from '@liveHelper/assets/Minimize'
import { SETTINGS_MODAL } from '@liveHelper/components/OneToOneModal/modalIds'
import { RecordingStatement } from '@liveHelper/components/RecordingStatement'
import { CamButton } from '@liveHelper/components/shared/CamButton'
import { ChatButton } from '@liveHelper/components/shared/ChatButton'
import { EndCallButton } from '@liveHelper/components/shared/EndCallButton'
import { MicButton } from '@liveHelper/components/shared/MicButton'
import { SettingsButton } from '@liveHelper/components/shared/SettingsButton'
import { SwitchCameraButton } from '@liveHelper/components/shared/SwitchCameraButton'
import { TripleDotLoader } from '@liveHelper/components/shared/TripleDotLoader'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useDraggableContext } from '@liveHelper/context/DraggableContext'
import { useVisitorCanUseCamera } from '@liveHelper/hooks/useVisitorCanUseCamera'
import { selectors } from '@liveHelper/models/modal/modalRegistry'
import {
  actionsSelector,
  conversationSelector,
  paramsSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import {
  AGENT_AGORA_TRACK_PLAYER_ID,
  ASPECT_RATIO_LANDSCAPE,
} from '@oneToOne/shared/components/chat/constants'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding/FireworkBranding'
import { ActionButton } from '@oneToOne/shared/styles'
import { getNameInitials } from '@oneToOne/shared/utils/videoCall'
import type { IAgoraRTCRemoteUser, ILocalVideoTrack } from 'agora-rtc-sdk-ng'

import { Collapse } from '@core/assets/Collapse'
import { AudioVisualizer } from '@core/components/AudioVisualizer'
import { useBreakpoint } from '@core/components/Layout/Breakpoint/useBreakpoint'
import { useContextTranslation } from '@core/i18n'
import { isMobileWeb } from '@core/userAgent'
import { useScreenOrientation } from '@core/window/hooks'

import {
  AgentAgoraTrackPlayer,
  ButtonRow,
  CloseButtonWrapperChatVersion,
  CustomerAgoraTrackPlayer,
  CustomerAgoraTrackPlayerWrapper,
  OnCallModalContainer,
  PlayerInnerContainer,
  VideoCallContainer,
  VideoContainer,
  VideoFireworkLogoWrapper,
} from './OnCall.styles'
import { OnCallChat } from './OnCallChat'

interface OnCallProps {
  isMinimized: boolean
  agentRTCUser: IAgoraRTCRemoteUser | undefined
  localVideoTrack: ILocalVideoTrack | undefined
}

/**
 * A component for rendering a on call modal. Can be in a minimized and expanded
 * state.
 */
export const OnCall: FC<OnCallProps> = ({
  isMinimized,
  agentRTCUser,
  localVideoTrack,
}) => {
  const uiState = useStore(uiStateSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const { t } = useContextTranslation()

  // TODO(OTO-173): isMobileWeb has width < 360 condition, this logic may not
  // work on some devices with wider screen resolution or using landscape mode.
  return (
    <OnCallModalContainer
      tabIndex={0}
      aria-label={t('oneToOne:One-to-one chat')}
      isMinimized={isMinimized}
      uiState={uiState}
      role={isMinimized ? 'button' : undefined}
      onKeyDown={(e) => {
        if (isMinimized && e.key === 'Enter') {
          sendUIStateTransitionEvent('EXPAND')
        }
      }}
      onClick={() => {
        if (isMinimized) {
          sendUIStateTransitionEvent('EXPAND')
        }
      }}
    >
      {isMinimized ? (
        <MinimizedOnCall agentUser={agentRTCUser} />
      ) : (
        <ExpandedOnCall
          agentUser={agentRTCUser}
          localVideoTrack={localVideoTrack}
        />
      )}
    </OnCallModalContainer>
  )
}

interface AgentTrackPlayerProps {
  agentUser: IAgoraRTCRemoteUser | undefined
  isMinimized: boolean
}

/** A component to render the agent user tracks. */
export const AgentTrackPlayer: FC<AgentTrackPlayerProps> = ({
  agentUser,
  isMinimized,
}) => {
  const { t } = useContextTranslation()
  const conversation = useStore(conversationSelector)

  const agentVideoTrack = agentUser?.videoTrack
  const agentAudioTrack = agentUser?.audioTrack

  if (!agentUser) {
    return (
      <PlayerInnerContainer>
        <TripleDotLoader />
      </PlayerInnerContainer>
    )
  }

  return (
    <>
      <PlayerInnerContainer>
        <AgentAgoraTrackPlayer
          id={AGENT_AGORA_TRACK_PLAYER_ID}
          videoTrack={agentVideoTrack}
          audioTrack={agentAudioTrack}
        />
      </PlayerInnerContainer>
      {!agentVideoTrack ? (
        <PlayerInnerContainer>
          <AudioVisualizer
            text={getNameInitials(conversation?.agent?.name) || t('Agent')}
            src={conversation?.agent?.avatar_url}
            audioTrack={agentAudioTrack}
            size={isMinimized ? 64 : 124}
          />
        </PlayerInnerContainer>
      ) : null}
    </>
  )
}

interface MinimizedOnCallProps {
  agentUser: IAgoraRTCRemoteUser | undefined
}

/** A component for rendering the minimized on call modal. */
export const MinimizedOnCall: FC<MinimizedOnCallProps> = ({ agentUser }) => {
  const isMobileLayoutEnabled = isMobileWeb()
  const draggableContext = useDraggableContext()
  const listeners = draggableContext ? draggableContext?.listeners : {}
  return (
    <VideoContainer
      {...listeners}
      isMobileWeb={isMobileLayoutEnabled}
      isMinimized={true}
    >
      <AgentTrackPlayer agentUser={agentUser} isMinimized={true} />
    </VideoContainer>
  )
}

interface ExpandedOnCallProps {
  agentUser: IAgoraRTCRemoteUser | undefined
  localVideoTrack: ILocalVideoTrack | undefined
}

/** A component for rendering the expanded on call modal.  */
export const ExpandedOnCall: FC<ExpandedOnCallProps> = ({
  agentUser,
  localVideoTrack,
}) => {
  const chatChannelConfig = useChatChannelConfig()
  const { t } = useContextTranslation()
  const { lang } = useStore(paramsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const uiState = useStore(uiStateSelector)
  const isMobileLayoutEnabled = isMobileWeb() || uiState === 'on_call_expanded'
  const { width: containerWidth, height: containerHeight } = useBreakpoint()
  const doesContainerSupportLandscape =
    containerWidth / containerHeight > ASPECT_RATIO_LANDSCAPE
  const screenOrientation = useScreenOrientation()
  const isChatExpanded = Boolean(!isMobileLayoutEnabled)
  const { hideModal } = useStore(selectors.modalActionsSelector)
  const utmContent = useGetUtmContent()
  const isCameraButtonVisible = useVisitorCanUseCamera()

  // Hide the settings modal this component is unmounted.
  useEffect(() => {
    return () => {
      hideModal(SETTINGS_MODAL)
    }
  }, [hideModal])

  return (
    <>
      <VideoCallContainer isChatExpanded={isChatExpanded}>
        <CloseButtonWrapperChatVersion>
          <ActionButton
            aria-label={t(`oneToOne:Minimize Live Helper Dialog`)}
            actionType="neutral"
            buttonSize="sm"
            shape="square"
            onClick={(e) => {
              e.stopPropagation()

              isMobileLayoutEnabled
                ? sendUIStateTransitionEvent('MINIMIZE')
                : sendUIStateTransitionEvent('TOGGLE_CHAT')
            }}
            icon={
              isMobileLayoutEnabled ? (
                <Minimize width="16px" fill="white" label="minimize" />
              ) : (
                <Collapse width="12px" fill="white" label="collapse" />
              )
            }
          />
          {!isMobileWeb() && uiState === 'on_call_expanded' ? (
            <ActionButton
              aria-label={t(`oneToOne:Expand Live Helper Dialog`)}
              actionType="neutral"
              buttonSize="sm"
              shape="square"
              onClick={() => {
                sendUIStateTransitionEvent('EXPAND')
              }}
              icon={<Expand width="18px" fill="white" label="expand" />}
            />
          ) : null}
        </CloseButtonWrapperChatVersion>
        <VideoContainer
          isMobileWeb={isMobileLayoutEnabled}
          isMinimized={false}
          doesContainerSupportLandscape={doesContainerSupportLandscape}
        >
          <AgentTrackPlayer agentUser={agentUser} isMinimized={false} />
          <CustomerAgoraTrackPlayerWrapper
            // rr-block is used to block the visitor video track from showing up
            // in the monitoring replay
            className="rr-block"
            isMobilePortrait={
              isMobileLayoutEnabled && screenOrientation === 'portrait'
            }
          >
            <CustomerAgoraTrackPlayer videoTrack={localVideoTrack} />
          </CustomerAgoraTrackPlayerWrapper>
        </VideoContainer>
        <RecordingStatement />
        <ButtonRow isMobileWeb={isMobileLayoutEnabled}>
          {isCameraButtonVisible && <CamButton />}
          <MicButton />
          {isMobileLayoutEnabled ? (
            <>
              {isMobileWeb() ? <SwitchCameraButton /> : null}
              <ChatButton
                isMinimized={false}
                chatEnabled={false}
                showUnreadMessageNumber={true}
              />
            </>
          ) : (
            <SettingsButton />
          )}
          <EndCallButton />
        </ButtonRow>
        {chatChannelConfig?.widget_style?.firework_branding ? (
          <VideoFireworkLogoWrapper>
            <FireworkBranding utmContent={utmContent} lang={lang} />
          </VideoFireworkLogoWrapper>
        ) : null}
      </VideoCallContainer>
      {isChatExpanded ? <OnCallChat /> : null}
    </>
  )
}
